import {
  getSubstanceText,
  getTaskProgressText,
  TopBar,
  AppLayout,
  useGetTasksQuery,
  useGetTaskDelegationsQuery,
  FilesIcon,
  Mtds,
  useGetPlanSummariesQuery,
  QrCodeIcon,
  DuedateSummary,
  TaskScreenContent,
  BuildingIcon,
  CheckCircleIcon,
  DropIcon,
  FlagIcon,
  MapPinIcon,
  NumberCircleOneIcon,
  SlidersHorizontalIcon,
  TestTubeIcon,
  VirusIcon,
  PlanStatusTag,
  type ScreenHeaderProperty,
  type Task
} from 'prove-common'
import { Link, useSearchParams } from 'react-router'
import {
  formatPlanChain,
  getDetailsFromTasks,
  sortTaskByDuedate,
  isTaskCompleted
} from '../utils/taskHelper'
import { AppLogo } from '../components/app-logo/AppLogo'
import { SideBar } from '../components/side-bar/SideBar'
import { useState } from 'react'
import RegisterSampleModal from '../components/register-sample-modal/RegisterSampleModal'

export const TaskGroupScreen = () => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedTabIndex, setSelectedTabIndex] = useState(0)
  const [searchParams] = useSearchParams()
  const taskIds =
    searchParams
      .get('ids')
      ?.split(',')
      .map(Number)
      .filter((id) => Number.isInteger(id) && id > 0) ?? []
  const hasTaskIds = taskIds.length > 0
  const { data: plans } = useGetPlanSummariesQuery()
  const {
    data: unfilteredTasks,
    isPending: isPendingTasks,
    isError: isErrorTasks,
    error: tasksError
  } = useGetTasksQuery({ taskIds })

  const filteredTasks = unfilteredTasks?.filter(({ id }) =>
    taskIds?.includes(id)
  )
  const sortedTasks = sortTaskByDuedate(filteredTasks ?? [])
  const {
    data: delegationEntries,
    isPending: isPendingDelegations,
    isError: isErrorDelegations,
    error: delegationError
  } = useGetTaskDelegationsQuery(
    sortedTasks.map((task) => task.id),
    sortedTasks.length > 0
  )
  const delegations = new Map(delegationEntries)
  const details =
    sortedTasks.length > 0 ? getDetailsFromTasks(sortedTasks, plans) : undefined
  const progressText = getTaskProgressText(
    details?.samplesTaken,
    details?.samplesToTake
  )
  const headerProperties: ScreenHeaderProperty[] = details
    ? [
        {
          label: 'Status',
          icon: <FlagIcon aria-hidden />,
          value: details.planStatus ? (
            <PlanStatusTag status={details.planStatus} />
          ) : (
            '-'
          )
        },
        {
          label: 'Produkt',
          icon: <DropIcon aria-hidden />,
          value: details.product ?? '-'
        },
        {
          label: 'Prøve av',
          icon: <DropIcon aria-hidden />,
          value: details.matrix ?? '-'
        },
        {
          label: 'Analyser for',
          icon: <VirusIcon aria-hidden />,
          value: `${sortedTasks.length} analyser`
        },
        {
          label: 'Uttakssted',
          icon: <MapPinIcon aria-hidden />,
          value: details.sampntTypeName ?? '-'
        },
        {
          label: 'Prøver tatt',
          icon: <TestTubeIcon aria-hidden />,
          value: progressText
        }
      ]
    : []
  const detailProperties: ScreenHeaderProperty[] = details
    ? [
        {
          label: 'Plan',
          icon: <NumberCircleOneIcon aria-hidden />,
          value: formatPlanChain(details.planChain) ?? '-'
        },
        {
          label: 'Laboratorium',
          icon: <BuildingIcon aria-hidden />,
          value: details.labName ?? '-'
        },
        {
          label: 'Hvordan telle prøver',
          icon: <SlidersHorizontalIcon aria-hidden />,
          value: details.countSubSamples ? 'Delprøver' : 'Prøveposer'
        },
        {
          label: 'Krever nøyaktig posisjon',
          icon: <CheckCircleIcon aria-hidden />,
          value: details.locationRequired ? 'Ja' : 'Nei'
        }
      ]
    : []
  const breadcrumbItems = [
    { label: 'Oppgaver', to: '/tasks' },
    { label: details?.matrix, icon: <FilesIcon /> }
  ]
  const disableRegisterSample =
    filteredTasks?.every(
      (task) =>
        task.analysisTypeApprovalStatus === 'awaiting' ||
        task.analysisTypeApprovalStatus === 'declined'
    ) ?? false
  let registerSampleText = 'Registrer ny prøve på denne oppgaven'
  if (disableRegisterSample) {
    registerSampleText =
      'Det er ikke mulig å registrere prøver på oppgaver som avventer godkjenning av analyse, eller der analysen er avvist av laboratoriet.'
  }

  const ongoingTasks = sortedTasks.filter(
    (task) => isTaskCompleted(task.totalCount, task.quantity) !== true
  )
  const completedTasks = sortedTasks.filter(
    (task) => isTaskCompleted(task.totalCount, task.quantity) === true
  )

  const renderTaskCard = (task: Task) => {
    const samplesTaken = task.totalCount ?? 0
    const samplesToTake = task.quantity ?? 0
    const isCompleted = isTaskCompleted(task.totalCount, task.quantity) === true
    const hasDuedates =
      Array.isArray(task?.duedates) && task.duedates.length > 0
    const taskProgressText = getTaskProgressText(samplesTaken, samplesToTake)
    const className =
      isCompleted || hasDuedates ? 'bg-background-tinted' : undefined
    const color = isCompleted ? 'success' : hasDuedates ? 'info' : undefined

    return (
      <Mtds.Card
        key={task.id}
        as={Link}
        to={`/tasks/${task.id}`}
        data-color={color}
        className={className}
      >
        <Mtds.Grid data-gap='3'>
          <Mtds.Flex data-justify='space-between' data-align='center'>
            <Mtds.Heading data-size='xs' className='mr-auto'>
              {getSubstanceText(task?.analysisType)}
            </Mtds.Heading>
            <Mtds.Flex data-gap='2' data-align='center'>
              {task?.analysisTypeApprovalStatus === 'awaiting' && (
                <Mtds.Tag
                  data-color='warning'
                  data-tooltip='Avventer godkjenning fra lab'
                >
                  Avventer
                </Mtds.Tag>
              )}
              {task?.analysisTypeApprovalStatus === 'declined' && (
                <Mtds.Tag
                  data-color='danger'
                  data-tooltip={task.approvalComment ?? 'Avvist'}
                >
                  Avvist
                </Mtds.Tag>
              )}
            </Mtds.Flex>
          </Mtds.Flex>
          <Mtds.Flex
            data-nowrap
            data-align='center'
            data-justify='space-between'
          >
            <Mtds.Progress value={samplesTaken} max={samplesToTake} />
            <Mtds.Muted data-nowrap>
              <strong>{taskProgressText}</strong>{' '}
              {details?.countSubSamples ? 'delprøver' : 'prøveposer'}
            </Mtds.Muted>
          </Mtds.Flex>
          {hasDuedates && (
            <Mtds.Muted>
              <DuedateSummary duedates={task.duedates} />
            </Mtds.Muted>
          )}
        </Mtds.Grid>
      </Mtds.Card>
    )
  }

  const renderTaskList = () => {
    if (sortedTasks.length === 0) {
      return (
        <Mtds.Card>
          <Mtds.Muted>Ingen analyser funnet for denne oppgaven.</Mtds.Muted>
        </Mtds.Card>
      )
    }

    return (
      <>
        <Mtds.Heading as='h2' data-size='sm'>
          Analyser for
        </Mtds.Heading>
        <Mtds.Togglegroup
          {...({ focusgroup: 'radiogroup' } as Record<string, string>)}
          aria-label='Status for analyser'
        >
          <Mtds.Togglegroup.Item
            checked={selectedTabIndex === 0}
            name='task-filter'
            value='oppgaver'
            onChange={() => setSelectedTabIndex(0)}
          >
            Analyser ({ongoingTasks.length})
          </Mtds.Togglegroup.Item>
          <Mtds.Togglegroup.Item
            checked={selectedTabIndex === 1}
            name='task-filter'
            value='ferdig'
            onChange={() => setSelectedTabIndex(1)}
          >
            Ferdig ({completedTasks.length})
          </Mtds.Togglegroup.Item>
        </Mtds.Togglegroup>
        {selectedTabIndex === 0 && ongoingTasks.length > 0 && (
          <Mtds.Grid>{ongoingTasks.map(renderTaskCard)}</Mtds.Grid>
        )}
        {selectedTabIndex === 0 && ongoingTasks.length === 0 && (
          <Mtds.Card>
            <Mtds.Muted>Ingen pågående analyser.</Mtds.Muted>
          </Mtds.Card>
        )}
        {selectedTabIndex === 1 && completedTasks.length > 0 && (
          <Mtds.Grid>{completedTasks.map(renderTaskCard)}</Mtds.Grid>
        )}
        {selectedTabIndex === 1 && completedTasks.length === 0 && (
          <Mtds.Card>
            <Mtds.Muted>Ingen ferdige analyser.</Mtds.Muted>
          </Mtds.Card>
        )}
      </>
    )
  }

  return (
    <AppLayout
      isError={isErrorTasks || isErrorDelegations}
      error={tasksError ?? delegationError}
      sidebar={<SideBar />}
      topBar={<TopBar logo={<AppLogo />} breadcrumbItems={breadcrumbItems} />}
      isLoading={hasTaskIds && (isPendingTasks || isPendingDelegations)}
      isFullWidth
    >
      {details ? (
        <TaskScreenContent
          title={details.matrix ?? 'Oppgave'}
          headerProperties={headerProperties}
          detailProperties={detailProperties}
        >
          <Mtds.Card as={Mtds.Grid}>
            <Mtds.Muted>{registerSampleText}</Mtds.Muted>
            <div>
              <Mtds.Button
                disabled={disableRegisterSample}
                data-variant='primary'
                onClick={() => setIsModalOpen(true)}
              >
                <QrCodeIcon />
                Registrer prøve
              </Mtds.Button>
            </div>
          </Mtds.Card>
          {sortedTasks.some(
            (t) => t.analysisTypeApprovalStatus === 'awaiting'
          ) && (
            <Mtds.Alert data-color={'warning'}>
              <Mtds.Heading>
                Denne kombinasjonen avventer godkjenning fra laboratoriet
              </Mtds.Heading>
            </Mtds.Alert>
          )}
          {(() => {
            const declinedTask = sortedTasks.find(
              (t) => t.analysisTypeApprovalStatus === 'declined'
            )
            return declinedTask ? (
              <Mtds.Alert data-color={'danger'}>
                <Mtds.Heading>Avvist analyse</Mtds.Heading>
                <p>
                  {declinedTask.approvalComment ??
                    'Laboratoriet har avvist analysen for noen av oppgavene.'}
                </p>
              </Mtds.Alert>
            ) : null
          })()}
          {renderTaskList()}
          <RegisterSampleModal
            isOpen={isModalOpen}
            setOpen={setIsModalOpen}
            tasks={sortedTasks}
            delegations={delegations}
          />
        </TaskScreenContent>
      ) : (
        <Mtds.Grid data-gap='8'>{renderTaskList()}</Mtds.Grid>
      )}
    </AppLayout>
  )
}
